import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { Button, Separator } from '@/components/ui'
import { useSEO, createArticleSchema } from '@/hooks/useSEO'
import { useScrollReveal, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { BeforeAfterSlider } from '@/components/BeforeAfterSlider'

export default function HolyTrinityCaseStudyPage() {
  const breadcrumbs = generateBreadcrumbs('/blog/holy-trinity-case-study', breadcrumbLabels)

  const heroRef = useHeroReveal<HTMLElement>(0.2)
  const sectionsRef = useScrollReveal<HTMLElement>('.article-section', { stagger: 0.1 })

  useSEO({
    title: 'How We Moved a Church Off WordPress Without Touching Their Email',
    description:
      'A bespoke rebuild for Holy Trinity Covina: off WordPress, onto a fast static site that scores 96/100 on The Full Score, with the parish email untouched through the cutover.',
    url: '/blog/holy-trinity-case-study',
    canonical: '/blog/holy-trinity-case-study',
    type: 'article',
    breadcrumbs,
    article: {
      publishedTime: '2026-06-20',
      author: 'Garrett John Law',
      section: 'Case Studies',
      tags: ['website rebuild', 'wordpress migration', 'church music', 'the full score', 'static site'],
    },
    schema: createArticleSchema({
      headline: 'How We Moved a Church Off WordPress Without Touching Their Email',
      description:
        'A bespoke rebuild for Holy Trinity Covina: off WordPress, onto a fast static site graded against The Full Score, with the parish email untouched.',
      datePublished: '2026-06-20',
      url: '/blog/holy-trinity-case-study',
    }),
  })

  return (
    <article ref={sectionsRef} className="mx-auto max-w-3xl space-y-12 px-4 py-16">
      {/* Header */}
      <header ref={heroRef} className="space-y-4">
        <Link to="/blog" className="text-primary text-sm hover:underline">
          &larr; Back to Blog
        </Link>
        <h1 className="text-fluid-h2">How We Moved a Church Off WordPress Without Touching Their Email</h1>
        <div className="text-muted-foreground flex items-center gap-4 text-sm">
          <time dateTime="2026-06-20">June 20, 2026</time>
          <span>&middot;</span>
          <span>6 min read</span>
        </div>
        <p className="text-fluid-h4 text-muted-foreground">
          Holy Trinity Covina needed people to find service times, find the music program, and sign up to sing. The
          website was getting in the way. Here is how we rebuilt it.
        </p>
      </header>

      {/* Cover: drag-to-compare hero */}
      <figure className="not-prose">
        <BeforeAfterSlider
          beforeSrc="/images/blog/holy-trinity/before-home.webp"
          afterSrc="/images/blog/holy-trinity/after-home.webp"
          beforeAlt="The old Holy Trinity Covina homepage on WordPress: the stone church building above a dated, cluttered navigation menu"
          afterAlt="The rebuilt Holy Trinity Covina homepage: a clean navigation bar over the congregation gathered outside the church, with a headline and an Upcoming Events button"
          beforeLabel="WordPress"
          afterLabel="Rebuilt"
        />
        <figcaption className="text-muted-foreground mt-3 text-sm">
          Drag the handle to compare: the old WordPress homepage and the rebuild, same screen.
        </figcaption>
      </figure>

      <Separator />

      {/* The Goal */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">Start With the Goal, Not the Website</h2>
        <p>
          Full disclosure: this is the parish where I serve as organist and choirmaster, so I see the website from both
          sides. I know what visitors are looking for, and I know what the staff dreads touching.
        </p>
        <p>A church website has a short list of jobs. For Holy Trinity, the real ones were:</p>
        <ul className="list-disc space-y-2 pl-6">
          <li>A newcomer can find when services are and what to expect before they walk in.</li>
          <li>Someone who loves to sing can figure out how to join a choir.</li>
          <li>The office can update a time or an event without calling for help.</li>
          <li>None of it puts the parish email at risk.</li>
        </ul>
        <p>
          That last one matters more than it sounds. The website and the email both run off the same domain, and a
          church that loses its email for a day loses its connection to the congregation. So the goal was never &ldquo;a
          prettier site.&rdquo; It was a site that does those four jobs and stays out of everyone&apos;s way.
        </p>
      </section>

      <Separator />

      {/* The Problem */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">What WordPress Was Costing Them</h2>
        <p>
          The old site ran on WordPress for years. It worked, in the sense that it loaded and had pages. But it was
          quietly failing at the jobs above.
        </p>
        <p>
          The music program was buried. The choirs were spread across pages that nobody updated because updating them
          meant fighting the editor. Service times lived in a block that looked different from everything around it. And
          every month there was a bill for the privilege of all this.
        </p>
        <p>
          The bigger problem was ownership. The content lived inside a platform and a pile of plugins. If you ever
          wanted to leave, you took screenshots and started over. The parish did not own its own site in any real sense.
          It rented one.
        </p>
      </section>

      <Separator />

      {/* The Standard */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">We Graded It Against The Full Score</h2>
        <p>
          Before rebuilding anything, we run the site through{' '}
          <Link to="/how-we-measure" className="text-primary hover:underline">
            The Full Score
          </Link>
          , our six-pillar standard for whether a website actually performs: Brand &amp; Design, Message &amp;
          Persuasion, Technical Foundation, Integrations &amp; Workflow, Maintainability &amp; Ownership, and Conversion
          &amp; Growth.
        </p>
        <p>
          The point is not the letter grade. The point is the list of gaps, because the gaps are the work. For Holy
          Trinity the gaps were clear: a Technical Foundation dragged down by a slow, heavy template, a music program
          with no real funnel to join, and an Ownership problem baked into the platform itself.
        </p>
      </section>

      <Separator />

      {/* The Build */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">The Build</h2>

        <h3 className="mt-8 text-xl font-semibold">A static site they actually own</h3>
        <p>
          We rebuilt the site as a custom React build, hosted as static files on S3 behind CloudFront. No platform
          subscription, no plugins to patch, no editor fighting you, no lock-in. The hosting bill for a site like this
          is pennies a month instead of WordPress hosting plus a stack of plugins. The content is the parish&apos;s, in
          their repository, forever.
        </p>

        <h3 className="mt-8 text-xl font-semibold">The cutover that did not touch email</h3>
        <p>
          This was the part that had to be exactly right. The parish email runs on the same domain as the website. A
          normal migration moves the whole domain and prays the email records survive.
        </p>
        <p>
          We did it differently. DNS stayed where it was, at the existing provider, so the email records never moved. We
          pointed only the <code>www</code> address at the new CloudFront site and set the bare domain to redirect to
          it. The website changed. The mail kept flowing the entire time. Zero downtime, zero risk to the inbox.
        </p>

        <h3 className="mt-8 text-xl font-semibold">One place to sign up to sing</h3>
        <p>
          The choirs used to be scattered. We built a single &ldquo;Sing With Us&rdquo; page that brings them together,
          explains who each group is for, and gives an interested singer one clear next step instead of a scavenger
          hunt. That is the Conversion &amp; Growth pillar in practice: a real funnel for the thing the music program
          most needs, which is people.
        </p>

        <h3 className="mt-8 text-xl font-semibold">Contact that comes from the church</h3>
        <p>
          The contact form sends through Amazon SES from the church&apos;s own domain, so messages look like they come
          from the parish, not from a third-party form vendor. Small detail, but it is the difference between a site
          that feels owned and one that feels rented.
        </p>

        <p className="!mb-2">A few pages from the new site, top to bottom (scroll each to see the whole page):</p>
        <div className="not-prose grid gap-4 sm:grid-cols-3">
          <figure className="space-y-2">
            <div className="border-border h-96 overflow-y-auto rounded-lg border">
              <img
                src="/images/blog/holy-trinity/music-full.webp"
                alt="The full Worship and Music page: service times, how we worship, the liturgy explained, and a contact form"
                width={1200}
                height={5111}
                loading="lazy"
                className="w-full"
              />
            </div>
            <figcaption className="text-muted-foreground text-xs">Worship &amp; Music, all in one place.</figcaption>
          </figure>
          <figure className="space-y-2">
            <div className="border-border h-96 overflow-y-auto rounded-lg border">
              <img
                src="/images/blog/holy-trinity/sing-full.webp"
                alt="The full Sing With Us page: the choir mid-rehearsal, the parish and festival choir options, and how to join"
                width={1200}
                height={5778}
                loading="lazy"
                className="w-full"
              />
            </div>
            <figcaption className="text-muted-foreground text-xs">One clear path into the choir.</figcaption>
          </figure>
          <figure className="space-y-2">
            <div className="border-border h-96 overflow-y-auto rounded-lg border">
              <img
                src="/images/blog/holy-trinity/organ-full.webp"
                alt="The full Organ page: the instrument, its specification, and its history"
                width={1200}
                height={6048}
                loading="lazy"
                className="w-full"
              />
            </div>
            <figcaption className="text-muted-foreground text-xs">A page for the organ itself.</figcaption>
          </figure>
        </div>
      </section>

      <Separator />

      {/* The Result */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">The Result</h2>
        <p>
          The new site went live on holytrinitycovina.com in June 2026. We ran it back through the same Full Score
          checker. It scores 96 out of 100, an A, with perfect marks on performance, accessibility, mobile, and
          structured data. Those are the pillars that decide whether people can find you, load you fast, and use you on
          any device.
        </p>

        {/* Metrics */}
        <div className="my-8 grid grid-cols-3 gap-4">
          <div className="bg-muted/10 border-border rounded-lg border p-4 text-center">
            <p className="text-primary text-2xl font-bold">96/100</p>
            <p className="text-muted-foreground text-sm">Full Score grade (A)</p>
          </div>
          <div className="bg-muted/10 border-border rounded-lg border p-4 text-center">
            <p className="text-primary text-2xl font-bold">100</p>
            <p className="text-muted-foreground text-sm">Performance, a11y &amp; mobile</p>
          </div>
          <div className="bg-muted/10 border-border rounded-lg border p-4 text-center">
            <p className="text-primary text-2xl font-bold">0 min</p>
            <p className="text-muted-foreground text-sm">Email downtime in the cutover</p>
          </div>
        </div>

        <p>
          The parish stopped paying a monthly platform fee. The office can update content without dreading it. And the
          choirs finally have one front door for anyone who wants to walk in and sing.
        </p>
      </section>

      <Separator />

      {/* Lessons */}
      <section className="prose prose-invert article-section max-w-none space-y-4">
        <h2 className="text-fluid-h2">What This Means for Your Organization</h2>
        <p>
          Most music organizations are not failing because their website is ugly. They are failing because the site is
          slow, hard to update, and rented from a platform that owns their content. The music program, the thing the
          organization exists for, ends up buried three clicks deep.
        </p>
        <p>
          A rebuild is not about a new coat of paint. It is about owning your site, making it fast, and pointing it at
          the one or two things you actually need it to do. For a church that is people in the pews and people in the
          choir. For a festival it is applicants. The method is the same: grade honestly against a standard, fix the
          gaps that matter, and keep the lights on while you do it.
        </p>
      </section>

      <Separator />

      {/* CTA */}
      <section className="bg-primary/5 border-primary/20 article-section space-y-6 rounded-lg border p-8 text-center">
        <h3 className="text-2xl font-semibold">Want to See Your Own Full Score?</h3>
        <p className="text-muted-foreground mx-auto max-w-2xl">
          We grade your current site against the same six pillars and hand you the list of gaps, whether or not you ever
          hire us. If the gaps are worth fixing, we can rebuild it the way we did for Holy Trinity.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
          <m.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <Link to="/contact">
              <Button variant="default" size="lg" className="cursor-pointer">
                Get Your Full Score &rarr;
              </Button>
            </Link>
          </m.div>
          <m.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <Link to="/how-we-measure">
              <Button variant="outline" size="lg" className="cursor-pointer">
                How We Measure
              </Button>
            </Link>
          </m.div>
        </div>
      </section>

      <Separator />

      {/* Author Bio */}
      <section className="border-border border-t pt-8">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <p className="text-lg font-semibold">About the Author</p>
            <p className="text-muted-foreground mt-2">
              <strong>Garrett John Law</strong> is a software engineer and musician who builds digital platforms for
              festivals, schools, and arts organizations. He&apos;s an Interlochen alum and CIM graduate, currently
              serving as organist and choirmaster at Holy Trinity Episcopal Church in Covina, the parish in this case
              study.
            </p>
            <div className="mt-4">
              <Link to="/about" className="text-primary text-sm hover:underline">
                Learn More about Fugue Lab &rarr;
              </Link>
            </div>
          </div>
        </div>
      </section>
    </article>
  )
}
